import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Trash2, Sparkles, Image as ImageIcon } from 'lucide-react';
import {
  PLATFORMS, IMAGE_ASSET_TYPES, STORY_ASSET_TYPES, supportsAssetType, platformLabel,
} from '@/lib/clipPlatformFormats';

const DESIGN_ASSET_TYPES = [...IMAGE_ASSET_TYPES.filter((t) => t.key !== 'thumbnail'), ...STORY_ASSET_TYPES.filter((t) => t.mediaKind === 'image')];

/**
 * "Design" category — the entry point for a new image/post asset: pick
 * its type, which platforms it will ship to (each becomes its own
 * editable variant, see section 6), and whether to start blank, from an
 * uploaded image, or from a template. Reuses the same centralized
 * platform/format rules Clips' variant generator uses.
 */
export function DesignCreatePanel({ designs, activeDesignId, onSelectDesign, onCreateDesign, onDeleteDesign, onStartFromMedia, onStartFromTemplate }) {
  const [assetType, setAssetType] = useState('image_post');
  const [platforms, setPlatforms] = useState(['instagram']);
  const [startFrom, setStartFrom] = useState('blank');

  const togglePlatform = (key) => setPlatforms((ps) => (ps.includes(key) ? ps.filter((p) => p !== key) : [...ps, key]));
  const validPlatforms = platforms.filter((p) => supportsAssetType(p, assetType));

  return (
    <div className="space-y-4" data-testid="design-create-panel">
      <div className="space-y-3 p-3 rounded-lg border border-white/10 bg-white/[0.02]">
        <p className="text-xs font-medium text-white">New Design</p>
        <div>
          <label className="text-[11px] text-[#8A93A6] mb-1 block">Asset type</label>
          <Select value={assetType} onValueChange={setAssetType}>
            <SelectTrigger className="h-8 text-xs bg-white/5 border-white/10" data-testid="design-asset-type-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {DESIGN_ASSET_TYPES.map((t) => <SelectItem key={t.key} value={t.key}>{t.label} ({t.aspect})</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-[11px] text-[#8A93A6] mb-1 block">Platforms</label>
          <div className="grid grid-cols-2 gap-1.5">
            {PLATFORMS.map((p) => {
              const supported = supportsAssetType(p.key, assetType);
              return (
                <label key={p.key} className={`flex items-center gap-2 px-2 py-1.5 rounded-md border text-xs ${supported ? 'border-white/10 bg-white/[0.03] text-white cursor-pointer' : 'border-white/5 text-[#555E6E] cursor-not-allowed opacity-60'}`} data-testid={`design-platform-option-${p.key}`}>
                  <Checkbox checked={platforms.includes(p.key)} disabled={!supported} onCheckedChange={() => togglePlatform(p.key)} data-testid={`design-platform-checkbox-${p.key}`} />
                  {p.label}{!supported && <span className="text-[9px] ml-auto">N/A</span>}
                </label>
              );
            })}
          </div>
        </div>
        <div>
          <label className="text-[11px] text-[#8A93A6] mb-1 block">Start from</label>
          <div className="grid grid-cols-3 gap-1.5">
            {[['blank', 'Blank'], ['media', 'Media'], ['template', 'Template']].map(([key, label]) => (
              <Button key={key} size="sm" variant={startFrom === key ? 'default' : 'outline'} className={startFrom === key ? 'bg-[#3A7BFF] hover:bg-[#3A7BFF]/90 text-xs' : 'border-white/10 text-xs'} onClick={() => setStartFrom(key)} data-testid={`design-start-from-${key}`}>
                {label}
              </Button>
            ))}
          </div>
        </div>
        <Button
          size="sm" className="w-full bg-[#3A7BFF] hover:bg-[#3A7BFF]/90"
          disabled={validPlatforms.length === 0}
          onClick={() => {
            if (startFrom === 'media') { onStartFromMedia(assetType, validPlatforms); return; }
            if (startFrom === 'template') { onStartFromTemplate(); return; }
            onCreateDesign(assetType, validPlatforms);
          }}
          data-testid="design-create-button"
        >
          <Plus className="h-4 w-4 mr-2" />Create {validPlatforms.length > 1 ? `${validPlatforms.length} Variants` : 'Design'}
        </Button>
      </div>

      <div>
        <p className="text-[11px] uppercase tracking-wide text-[#555E6E] mb-1.5">Your Designs ({designs.length})</p>
        {designs.length === 0 ? (
          <p className="text-xs text-[#8A93A6] text-center py-6" data-testid="design-list-empty">No designs yet — create one above.</p>
        ) : (
          <div className="space-y-1.5">
            {designs.map((d) => (
              <div key={d.id} onClick={() => onSelectDesign(d.id)} className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer ${d.id === activeDesignId ? 'border-[#3A7BFF] bg-[#3A7BFF]/10' : 'border-white/10 hover:bg-white/5'}`} data-testid={`design-list-item-${d.id}`}>
                <ImageIcon className="h-3.5 w-3.5 text-[#8A93A6] shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-xs text-white truncate">{d.name}</p>
                  <p className="text-[10px] text-[#8A93A6]">{d.variants.length} variant{d.variants.length === 1 ? '' : 's'} · {d.variants.map((v) => platformLabel(v.platform)).join(', ')}</p>
                </div>
                <button onClick={(e) => { e.stopPropagation(); onDeleteDesign(d.id); }} className="text-red-400 hover:text-red-300 shrink-0" data-testid={`design-list-delete-${d.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            ))}
          </div>
        )}
      </div>
      <p className="text-[10px] text-[#555E6E] flex items-center gap-1"><Sparkles className="h-3 w-3 shrink-0" />Designs save to this browser for now (Level 3: server-side persistence).</p>
    </div>
  );
}
